"use client";

import { Switch } from "@mui/material";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import useToast from "@/contexts/toast-context";
import { buildErrorMessage } from "@/helpers/error-helpers";
import useGetAccountAnticipation from "@/hooks/requests/t2Pay/use-get-account-anticipation";
import useUpdateAccountAnticipation from "@/hooks/requests/t2Pay/use-update-account-anticipation";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";

type Props = {
  messages: T2PayHomeMessages;
};

function AnticipationToggle({ messages }: Props) {
  const { data, isLoading: isLoadingConfig } = useGetAccountAnticipation();
  const { update, isLoading } = useUpdateAccountAnticipation();
  const { showToast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorDescription, setErrorDescription] = useState("");

  const enabled = !!data?.creditCardAutomaticEnabled;
  const busy = isLoading || isLoadingConfig;

  const persist = async (nextEnabled: boolean) => {
    try {
      await update(nextEnabled);
      showToast({
        text: nextEnabled ? messages.anticipationEnabledToast : messages.anticipationDisabledToast,
        type: "success",
      });
      setConfirmOpen(false);
    } catch (error) {
      const apiMessage = buildErrorMessage(error, messages.anticipationGenericError);

      if (nextEnabled) {
        setConfirmOpen(false);
        setErrorDescription(apiMessage);
        setErrorOpen(true);
        return;
      }

      showToast({ text: apiMessage, type: "error" });
    }
  };

  const handleSwitchChange = (_: unknown, checked: boolean) => {
    if (checked) {
      setConfirmOpen(true);
      return;
    }

    void persist(false);
  };

  return (
    <>
      <MDBox display="flex" alignItems="center" gap={1}>
        <Switch checked={enabled} disabled={busy} onChange={handleSwitchChange} />
        <MDBox>
          <MDTypography variant="button" fontWeight="regular">
            {messages.anticipationToggleLabel}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            {messages.anticipationToggleHint}
          </MDTypography>
        </MDBox>
      </MDBox>

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void persist(true)}
        titleText={messages.anticipationConfirmTitle}
        descriptionText={messages.anticipationConfirmDescription}
        cancelText={messages.cancelButton}
        confirmText={messages.anticipationConfirmButton}
        isLoading={isLoading}
        minWidth={480}
        maxWidth={560}
      />

      <ConfirmationDialog
        isOpen={errorOpen}
        onClose={() => setErrorOpen(false)}
        onConfirm={() => setErrorOpen(false)}
        showConfirmButton={false}
        titleText={messages.anticipationErrorTitle}
        descriptionText={errorDescription}
        additionalDescriptionText={messages.anticipationErrorPrerequisites}
        cancelText={messages.anticipationErrorCloseButton}
        confirmText={messages.anticipationErrorCloseButton}
        minWidth={480}
        maxWidth={560}
      />
    </>
  );
}

export default AnticipationToggle;
